import { createSelector } from '@reduxjs/toolkit';
import { PAYMENT_STATUS_DELIVERED } from 'constants/payment';
import type { PaymentWithEntities } from 'models/payment';
import { ChargeIntent } from 'models/recurringPayment';
import type { RootState } from 'reducers/types';
import { getRecipientId, isRecipientCAP } from '../recipient/recipient';
import { getRecipientById } from '../recipients/recipients';
import { getUIPaymentStatus } from '../utils/payment/payment';

export const getPayments = (state: RootState) => {
  const {
    entities: { payments },
  } = state;

  return payments.map(payment => {
    const { id, purchase, recipient, createdAt, offer } = payment;
    const UIStatus = getUIPaymentStatus(recipient, payment);
    let recipientWithAllInfo = getRecipientById(state, recipient.id);

    if (isRecipientCAP(recipient)) {
      const { name, metadata } = recipientWithAllInfo || {};
      recipientWithAllInfo = { ...recipient, name, metadata };
    }

    return {
      id: id,
      status: UIStatus,
      purchase: purchase,
      recipient: recipientWithAllInfo ?? recipient,
      createdAt: createdAt,
      offer: offer,
      hasRefunds: !!getPaymentLink(payment, 'refunds'),
      chargeIntent: payment.chargeIntent,
    };
  });
};

export const getRecipientsIds = (state: RootState) => {
  const {
    entities: { payments },
  } = state;
  const ids = payments.map(payment => payment.recipient.id);
  const idsWithoutDuplicates = Array.from(new Set(ids));

  return idsWithoutDuplicates;
};

export const getPaymentById = (state: RootState, id: string) => {
  const {
    entities: { payments },
  } = state;

  return payments.find(payment => payment.id === id);
};

export const getRecipientIdByPaymentId = (
  state: RootState,
  paymentId: string,
): string | undefined => {
  const payment = getPaymentById(state, paymentId);
  return payment?.recipient?.id;
};

export const getLastMatchingPaymentFromRecipient = (state: RootState) => {
  const recipientId = getRecipientId(state);

  const {
    entities: { payments },
  } = state;

  return payments.find(payment => payment.recipient.id === recipientId);
};

const getPaymentLink = (payment: PaymentWithEntities, linkRel: string) => {
  return payment.links && payment.links.find(link => link.rel === linkRel);
};

const hasRecurringId = (payment: { chargeIntent?: ChargeIntent }) => {
  const recurringId = payment.chargeIntent?.recurringId;
  return !!recurringId && String(recurringId).trim() !== '';
};

type GatewayOrPrepopulatedMetadata = {
  gatewayOnly?: { enabled?: boolean };
  clientPrepopulated?: boolean;
};

const isPaymentGatewayOrClientPrepopulatedFromPayment = (payment: {
  recipient?: { metadata?: GatewayOrPrepopulatedMetadata };
}) => {
  const {
    recipient: {
      metadata: {
        gatewayOnly = { enabled: false },
        clientPrepopulated = false,
      } = {},
    } = {},
  } = payment;

  return Boolean(gatewayOnly?.enabled || clientPrepopulated);
};

const getRecipientsById = (state: RootState) =>
  state.entities?.recipients?.byId;

const isRecipientGatewayOrClientPrepopulatedById = (
  recipientsById:
    | Record<string, { metadata?: GatewayOrPrepopulatedMetadata }>
    | undefined,
  recipientId?: string,
): boolean => {
  if (!recipientId || !recipientsById) {
    return false;
  }
  const recipient = recipientsById[recipientId];
  return Boolean(
    recipient?.metadata?.gatewayOnly?.enabled ||
      recipient?.metadata?.clientPrepopulated,
  );
};

export const getPaymentGatewayOrClientPrepopulatedMap = createSelector(
  [(state: RootState) => state.entities.payments, getRecipientsById],
  (payments, recipientsById) =>
    Object.fromEntries(
      payments.map(p => [
        p.id,
        isPaymentGatewayOrClientPrepopulatedFromPayment(p) ||
          isRecipientGatewayOrClientPrepopulatedById(
            recipientsById,
            p.recipient?.id,
          ),
      ]),
    ) as Record<string, boolean>,
);

export const isPaymentGatewayOrClientPrepopulated = (
  state: RootState,
  paymentId: string,
) => getPaymentGatewayOrClientPrepopulatedMap(state)[paymentId] ?? false;

export const canDuplicatePayment = (state: RootState, paymentId: string) => {
  const payment = getPayments(state).find(({ id }) => id === paymentId);

  if (!payment) {
    return false;
  }

  if (hasRecurringId(payment)) {
    return false;
  }

  const { status } = payment;

  return status === PAYMENT_STATUS_DELIVERED;
};
